import { useState, useMemo } from "react";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

// R in L·atm·mol⁻¹·K⁻¹ — matches the atm / L / mol / K unit set below.
const R = 0.0820573;

// ── Ideal gas law: PV = nRT. Given any 3, solve the 4th. ──
const IDEAL_FIELDS = [
  { k: "P", label: "Pressure (P)", unit: "atm" },
  { k: "V", label: "Volume (V)", unit: "L" },
  { k: "n", label: "Moles (n)", unit: "mol" },
  { k: "T", label: "Temperature (T)", unit: "K" },
];
function solveIdeal(v) {
  const present = IDEAL_FIELDS.filter(f => v[f.k] !== null);
  if (present.length !== 3) return null;
  if (present.some(f => isNaN(v[f.k]))) return null;
  // P, V, n and absolute T (Kelvin) are all physically positive. Reject
  // a negative/zero filled input even when the solved value comes out
  // positive (e.g. V=-1, n=-1, T=300 → P>0). Guard before solving.
  if (present.some(f => v[f.k] <= 0))
    return { err: "P, V, n and T (K) must be positive numbers" };
  const miss = IDEAL_FIELDS.find(f => v[f.k] === null).k;
  let val;
  if (miss === "P") { if (v.V === 0) return { err: "V can't be 0" }; val = (v.n * R * v.T) / v.V; }
  else if (miss === "V") { if (v.P === 0) return { err: "P can't be 0" }; val = (v.n * R * v.T) / v.P; }
  else if (miss === "n") { if (v.T === 0) return { err: "T can't be 0" }; val = (v.P * v.V) / (R * v.T); }
  else { if (v.n === 0) return { err: "n can't be 0" }; val = (v.P * v.V) / (v.n * R); }
  if (!isFinite(val) || val < 0) return { err: "No valid positive result" };
  return { miss, val };
}

// ── Combined gas law: P₁V₁/T₁ = P₂V₂/T₂. Given any 5, solve the 6th. ──
const COMB_FIELDS = [
  { k: "P1", label: "P₁", unit: "atm" }, { k: "V1", label: "V₁", unit: "L" }, { k: "T1", label: "T₁", unit: "K" },
  { k: "P2", label: "P₂", unit: "atm" }, { k: "V2", label: "V₂", unit: "L" }, { k: "T2", label: "T₂", unit: "K" },
];
function solveCombined(v) {
  const present = COMB_FIELDS.filter(f => v[f.k] !== null);
  if (present.length !== 5) return null;
  if (present.some(f => isNaN(v[f.k]))) return null;
  // All P, V and absolute T (Kelvin) are physically positive — reject a
  // negative/zero filled input even if the solved value is positive.
  if (present.some(f => v[f.k] <= 0))
    return { err: "Pressures, volumes and T (K) must be positive numbers" };
  const m = COMB_FIELDS.find(f => v[f.k] === null).k;
  let val;
  try {
    if (m === "P2") val = (v.P1 * v.V1 * v.T2) / (v.T1 * v.V2);
    else if (m === "V2") val = (v.P1 * v.V1 * v.T2) / (v.T1 * v.P2);
    else if (m === "T2") val = (v.P2 * v.V2 * v.T1) / (v.P1 * v.V1);
    else if (m === "P1") val = (v.P2 * v.V2 * v.T1) / (v.T2 * v.V1);
    else if (m === "V1") val = (v.P2 * v.V2 * v.T1) / (v.T2 * v.P1);
    else val = (v.P1 * v.V1 * v.T2) / (v.P2 * v.V2);
  } catch { return null; }
  if (!isFinite(val) || val < 0) return { err: "No valid positive result" };
  return { miss: m, val };
}

const fmt = (x) => formatNum(x, 5);

export default function GasLaws() {
  const [mode, setMode] = useState("ideal");
  const [iv, setIv] = useState({ P: "", V: "", n: "", T: "" });
  const [cv, setCv] = useState({ P1: "", V1: "", T1: "", P2: "", V2: "", T2: "" });

  const toNum = (o) => {
    const r = {};
    for (const k in o) r[k] = o[k] === "" ? null : (isFinite(parseFloat(o[k])) ? parseFloat(o[k]) : NaN);
    return r;
  };

  const idealResult = useMemo(() => mode === "ideal" ? solveIdeal(toNum(iv)) : null, [iv, mode]);
  const combResult = useMemo(() => mode === "combined" ? solveCombined(toNum(cv)) : null, [cv, mode]);

  const fields = mode === "ideal" ? IDEAL_FIELDS : COMB_FIELDS;
  const vals = mode === "ideal" ? iv : cv;
  const setVals = mode === "ideal" ? setIv : setCv;
  const result = mode === "ideal" ? idealResult : combResult;

  const inStyle = (active) => ({ width: "100%", padding: "13px 15px", borderRadius: 10, border: active ? `1.5px solid ${rgba(EM,0.4)}` : `1px solid ${rgba(EM,0.15)}`, background: active ? rgba(EM,0.06) : "rgba(0,0,0,0.3)", color: active ? EM_LIGHT : "#E2E8F0", fontSize: 16, fontFamily: "inherit", outline: "none", boxSizing: "border-box", fontWeight: active ? 700 : 400 });

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #FDE9C8)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Gas Law Solver</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 560, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 22px" }}>
          Solve the ideal gas law (PV = nRT) or the combined gas law. Leave
          exactly one field blank — it's calculated. Use atm, L, mol, and
          kelvin.
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
          {[["ideal", "Ideal — PV = nRT"], ["combined", "Combined — P₁V₁/T₁ = P₂V₂/T₂"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setMode(k)} style={{ flex: 1, padding: "11px 6px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: mode === k ? rgba(EM,0.12) : "rgba(255,255,255,0.02)", border: mode === k ? `1.5px solid ${rgba(EM,0.35)}` : "1.5px solid rgba(255,255,255,0.05)", color: mode === k ? EM_LIGHT : "#6B7280" }}>{lbl}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: mode === "combined" ? "1fr 1fr" : "1fr", gap: 14 }}>
          {fields.map(f => {
            const isSolved = result && !result.err && result.miss === f.k;
            return (
              <div key={f.k}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{f.label}</label>
                <div style={{ position: "relative", marginTop: 6 }}>
                  <input
                    type="number"
                    value={isSolved ? Number(result.val.toPrecision(6)) : vals[f.k]}
                    onChange={(e) => setVals(s => ({ ...s, [f.k]: e.target.value }))}
                    readOnly={isSolved}
                    placeholder={isSolved ? "" : f.unit}
                    style={inStyle(isSolved)}
                  />
                  <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280", pointerEvents: "none" }}>{f.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {result?.err && (
          <div style={{ marginTop: 22, padding: "14px 18px", borderRadius: 12, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#FDA4AF", fontSize: 14, textAlign: "center" }}>{result.err}</div>
        )}
        {result && !result.err && (
          <div style={{ marginTop: 22, padding: "18px", borderRadius: 14, background: rgba(EM,0.07), border: `1px solid ${rgba(EM,0.25)}`, textAlign: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Solved</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: EM_LIGHT }}>
              {fields.find(f => f.k === result.miss).label} = {fmt(result.val)} <span style={{ fontSize: 14, color: "#6B7280" }}>{fields.find(f => f.k === result.miss).unit}</span>
            </div>
          </div>
        )}
        <p style={{ fontSize: 12, color: "#4B5563", textAlign: "center", marginTop: 18, lineHeight: 1.5 }}>
          Temperature must be in kelvin (K = °C + 273.15). R = 0.082057 L·atm·mol⁻¹·K⁻¹.
        </p>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;} * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
